// Settings → Data Export (Wave E).
//
// Admin-only. Wrapped in <SubscriptionGate feature="data-export"> by the
// router. Exports a tenant's records from the existing list endpoints
// (no new server endpoint) into CSV files the user downloads locally.
//
// Three exports in v1: Customers, Bookings, Receipts. Each kicks off a
// paged crawl of the relevant /api/v1 list endpoint, accumulates the
// records, and writes a UTF-8 CSV via a programmatic anchor click.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { useToast } from '../../../../hooks/useToast';
import styles from './DataExportPage.module.css';

interface ExportConfig {
  key: 'customers' | 'bookings' | 'receipts';
  label: string;
  description: string;
  endpoint: string;
  recordsPath: string;
  filename: string;
  columns: ReadonlyArray<{ key: string; header: string }>;
}

const EXPORTS: ExportConfig[] = [
  {
    key: 'customers',
    label: 'Customers',
    description: 'All customer records, including contact and address fields.',
    endpoint: endpoints.customers.list,
    recordsPath: 'customers',
    filename: 'customers.csv',
    columns: [
      { key: 'id', header: 'id' },
      { key: 'uuid', header: 'uuid' },
      { key: 'name', header: 'name' },
      { key: 'email', header: 'email' },
      { key: 'phone', header: 'phone' },
      { key: 'address', header: 'address' },
      { key: 'postcode', header: 'postcode' },
      { key: 'created_at', header: 'created_at' },
    ],
  },
  {
    key: 'bookings',
    label: 'Bookings',
    description: 'Every booking, including status, price, and collection details.',
    endpoint: endpoints.bookings.list,
    recordsPath: 'bookings',
    filename: 'bookings.csv',
    columns: [
      { key: 'reference', header: 'reference' },
      { key: 'unique_id', header: 'unique_id' },
      { key: 'status', header: 'status' },
      { key: 'name', header: 'customer_name' },
      { key: 'phone', header: 'customer_phone' },
      { key: 'car_make_model', header: 'vehicle' },
      { key: 'car_registration', header: 'registration' },
      { key: 'price', header: 'price' },
      { key: 'date', header: 'date' },
      { key: 'timeframe', header: 'timeframe' },
      { key: 'driver_name', header: 'driver' },
      { key: 'created_at', header: 'created_at' },
    ],
  },
  {
    key: 'receipts',
    label: 'Receipts',
    description: 'COVD receipts including amount, customer and vehicle.',
    endpoint: endpoints.receipts.list,
    recordsPath: 'receipts',
    filename: 'receipts.csv',
    columns: [
      { key: 'reference', header: 'reference' },
      { key: 'unique_id', header: 'unique_id' },
      { key: 'customer_name', header: 'customer_name' },
      { key: 'phone', header: 'customer_phone' },
      { key: 'car_make_model', header: 'vehicle' },
      { key: 'car_registration', header: 'registration' },
      { key: 'amount', header: 'amount' },
      { key: 'date', header: 'date' },
      { key: 'created_at', header: 'created_at' },
    ],
  },
];

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value : String(value);
  // RFC 4180: wrap any cell containing a comma, quote or newline in quotes
  // and escape embedded quotes by doubling them.
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function rowsToCsv(
  rows: ReadonlyArray<Record<string, unknown>>,
  columns: ReadonlyArray<{ key: string; header: string }>,
): string {
  const header = columns.map((c) => escapeCsvCell(c.header)).join(',');
  const lines = rows.map((row) =>
    columns.map((c) => escapeCsvCell(row[c.key])).join(','),
  );
  return [header, ...lines].join('\r\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

interface PaginatedListResponse<T> {
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  [k: string]: unknown | T[];
}

async function fetchAllPaginated<T extends Record<string, unknown>>(
  endpoint: string,
  recordsPath: string,
  signalAbort: AbortController,
): Promise<T[]> {
  const all: T[] = [];
  const limit = 100;
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages && !signalAbort.signal.aborted) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    // eslint-disable-next-line no-await-in-loop
    const res = await apiClient.get<PaginatedListResponse<T>>(
      `${endpoint}?${params.toString()}`,
    );
    const records = (res.data[recordsPath] as T[] | undefined) ?? [];
    all.push(...records);
    totalPages = res.data.pagination?.totalPages ?? 1;
    page += 1;
  }
  return all;
}

export function DataExportPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  async function handleExport(config: ExportConfig) {
    setBusyKey(config.key);
    const ctrl = new AbortController();
    try {
      const rows = await fetchAllPaginated<Record<string, unknown>>(
        config.endpoint,
        config.recordsPath,
        ctrl,
      );
      const csv = rowsToCsv(rows, config.columns);
      downloadCsv(config.filename, csv);
      toast.success(`${config.label}: ${rows.length} record(s) exported.`);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : `Failed to export ${config.label.toLowerCase()}.`;
      toast.error(message);
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Data Export"
        subtitle="Download your tenant's records as CSV files"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/reports')}
          >
            Back
          </Button>
        }
      />

      <Card>
        <p className={styles.note}>
          Exports run live against the API and may take a few seconds for
          large tenants. Files are written in UTF-8 with a BOM so spreadsheet
          apps render special characters correctly. All exports are scoped
          to your company.
        </p>

        <ul className={styles.exports}>
          {EXPORTS.map((cfg) => {
            const busy = busyKey === cfg.key;
            return (
              <li key={cfg.key} className={styles.exportItem}>
                <div className={styles.itemMain}>
                  <FileText size={20} aria-hidden />
                  <div>
                    <h3>{cfg.label}</h3>
                    <p>{cfg.description}</p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  icon={busy ? Loader2 : Download}
                  loading={busy}
                  disabled={busyKey !== null && !busy}
                  onClick={() => void handleExport(cfg)}
                >
                  Export
                </Button>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
